import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TicketLayout } from "@jambushrushti/components/tickets/TicketLayout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  useTickets,
  useTicketCategories,
  useAdminUsers,
} from "@jambushrushti/hooks/useTickets";
import { TicketCard } from "@jambushrushti/components/tickets/TicketCard";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { Plus, Search, Filter, Ticket } from "lucide-react";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";

export default function TicketList() {
  const [filters, setFilters] = useState({
    status: "",
    category: "",
    priority: "",
    search: "",
  });

  const { data: categories = [] } = useTicketCategories();
  const { data: adminUsers = [] } = useAdminUsers();
  const { data: tickets, isLoading } = useTickets({
    status: filters.status || undefined,
    category: filters.category || undefined,
    priority: filters.priority || undefined,
    search: filters.search || undefined,
  });

  // Create a map of user IDs to names for quick lookup
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    adminUsers.forEach(user => {
      map[user.id] = user.name;
    });
    return map;
  }, [adminUsers]);

  const clearFilters = () => {
    setFilters({ status: "", category: "", priority: "", search: "" });
  };

  const hasFilters =
    filters.status || filters.category || filters.priority || filters.search;

  return (
    <TicketLayout title="All Tickets">
      <Seo
        title="All Tickets | Jambushrusti"
        description="View and manage all support tickets."
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              All Tickets
            </h1>
            <p className="text-muted-foreground">
              {tickets?.length || 0} tickets found
            </p>
          </div>
          <Button asChild>
            <Link to="/tickets/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Ticket
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-5">
              {/* Search */}
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by ID or title..."
                  className="pl-9"
                  value={filters.search}
                  onChange={e =>
                    setFilters(prev => ({ ...prev, search: e.target.value }))
                  }
                />
              </div>

              {/* Status */}
              <Select
                value={filters.status}
                onValueChange={value =>
                  setFilters(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              {/* Category */}
              <Select
                value={filters.category}
                onValueChange={value =>
                  setFilters(prev => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Priority */}
              <Select
                value={filters.priority}
                onValueChange={value =>
                  setFilters(prev => ({ ...prev, priority: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-28 w-full" />
              ))}
            </div>
          ) : tickets && tickets.length > 0 ? (
            tickets.map(ticket => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                assigneeName={
                  ticket.assigned_to
                    ? userNameMap[ticket.assigned_to]
                    : undefined
                }
              />
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Ticket className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
                <p className="mb-4 text-muted-foreground">
                  {hasFilters
                    ? "No tickets match your filters."
                    : "No tickets found."}
                </p>
                {hasFilters ? (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                ) : (
                  <Button asChild>
                    <Link to="/tickets/create">Create First Ticket</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </TicketLayout>
  );
}
